import React, { useState, useEffect } from 'react';
import { stockAcknowledgmentAPI, userAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  ClipboardCheck, 
  Loader2, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Eye,
  Search,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';

const StockAcknowledgmentPage = () => {
  const [pendingIssues, setPendingIssues] = useState([]);
  const [disputedIssues, setDisputedIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [acknowledgeDialogOpen, setAcknowledgeDialogOpen] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Form states
  const [acknowledgeNotes, setAcknowledgeNotes] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [resolution, setResolution] = useState('');
  const [resolveAction, setResolveAction] = useState('accept');
  const [itemAcknowledgments, setItemAcknowledgments] = useState({});
  
  // Check if user is Super Admin
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    fetchData();
    
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setIsSuperAdmin(user?.role_name === 'Super Admin');
      }
    } catch (e) {
      console.error('Error parsing user:', e);
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pendingRes, disputedRes] = await Promise.all([
        stockAcknowledgmentAPI.getPending(),
        stockAcknowledgmentAPI.getDisputed().catch(() => ({ data: [] }))
      ]);
      setPendingIssues(pendingRes.data);
      setDisputedIssues(disputedRes.data || []);
    } catch (error) {
      toast.error('Failed to load stock issues');
    } finally {
      setLoading(false);
    }
  };

  const handleViewIssue = (issue) => {
    setSelectedIssue(issue);
    setViewDialogOpen(true);
  };

  const handleOpenDispute = (issue) => {
    setSelectedIssue(issue);
    setDisputeReason('');
    setDisputeDialogOpen(true);
  };

  const handleOpenResolve = (issue) => {
    setSelectedIssue(issue);
    setResolution('');
    setResolveAction('accept');
    setResolveDialogOpen(true);
  };

  const handleAcknowledge = async () => {
    try {
      setProcessing(true);
      await stockAcknowledgmentAPI.acknowledge(selectedIssue.id, { notes: acknowledgeNotes });
      toast.success('Stock acknowledged successfully');
      setAcknowledgeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge stock');
    } finally {
      setProcessing(false);
    }
  };

  // Initialize item acknowledgments when dialog opens
  const handleOpenAcknowledge = (issue) => {
    setSelectedIssue(issue);
    setAcknowledgeNotes('');
    
    // Initialize item acknowledgments with all items accepted
    const initialAcks = {};
    issue.items?.forEach(item => {
      initialAcks[item.item_id] = {
        action: 'accept',
        received_quantity: item.quantity,
        notes: ''
      };
    });
    setItemAcknowledgments(initialAcks);
    setAcknowledgeDialogOpen(true);
  };

  // Handle item acknowledgment change
  const handleItemAckChange = (itemId, field, value) => {
    setItemAcknowledgments(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  // Accept all items
  const handleAcknowledgeAll = async () => {
    try {
      setProcessing(true);
      await stockAcknowledgmentAPI.acknowledge(selectedIssue.id, { notes: acknowledgeNotes });
      toast.success('All items acknowledged successfully');
      setAcknowledgeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge stock');
    } finally {
      setProcessing(false);
    }
  };

  // Item-wise acknowledgment
  const handleAcknowledgeItemWise = async () => {
    try {
      setProcessing(true);
      
      const itemsToAck = selectedIssue.items?.map(item => {
        const ack = itemAcknowledgments[item.item_id] || { action: 'accept', received_quantity: item.quantity };
        return {
          item_id: item.item_id,
          action: ack.action,
          received_quantity: ack.action === 'reject' ? 0 : ack.received_quantity,
          notes: ack.notes
        };
      }) || [];
      
      const response = await stockAcknowledgmentAPI.acknowledgeItems(selectedIssue.id, {
        items: itemsToAck,
        notes: acknowledgeNotes
      });
      
      toast.success(response.data.message || 'Stock acknowledged successfully');
      setAcknowledgeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge stock');
    } finally {
      setProcessing(false);
    }
  };

  const handleDispute = async () => {
    if (!disputeReason.trim()) {
      toast.error('Please provide a reason for the dispute');
      return;
    }
    
    try {
      setProcessing(true);
      await stockAcknowledgmentAPI.dispute(selectedIssue.id, { reason: disputeReason });
      toast.success('Stock issue disputed. Admin will review.');
      setDisputeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispute stock');
    } finally {
      setProcessing(false);
    }
  };

  const handleResolve = async () => {
    if (!resolution.trim()) {
      toast.error('Please provide resolution details');
      return;
    }
    
    try {
      setProcessing(true);
      await stockAcknowledgmentAPI.resolveDispute(selectedIssue.id, {
        resolution,
        action: resolveAction
      });
      toast.success(`Dispute resolved with action: ${resolveAction}`);
      setResolveDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve dispute');
    } finally {
      setProcessing(false);
    }
  };

  // Filter issues
  const filteredPending = pendingIssues.filter(issue => 
    !searchQuery || 
    issue.transaction_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    issue.from_store_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDisputed = disputedIssues.filter(issue => 
    !searchQuery || 
    issue.transaction_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    issue.from_store_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'acknowledged':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Acknowledged</Badge>;
      case 'disputed':
        return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" /> Disputed</Badge>;
      case 'rejected':
        return <Badge variant="secondary"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline"><Package className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="stock-acknowledgment-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 md:h-7 md:w-7" />
            Stock Acknowledgment
          </h1>
          <p className="text-muted-foreground">Review and acknowledge stock received at your store</p>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by issue number or store..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" className="gap-2">
            <Package className="h-4 w-4" />
            Pending ({filteredPending.length})
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="disputed" className="gap-2">
              <AlertTriangle className="h-4 w-4" />
              Disputed ({filteredDisputed.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Pending Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pending Acknowledgment</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredPending.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No pending stock issues to acknowledge</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Issue #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>From Store</TableHead>
                      <TableHead>To Store</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Issued By</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPending.map((issue) => (
                      <TableRow key={issue.id}>
                        <TableCell className="font-mono font-medium">{issue.transaction_number}</TableCell>
                        <TableCell>{formatDate(issue.date)}</TableCell>
                        <TableCell>{issue.from_store_name || '-'}</TableCell>
                        <TableCell>{issue.to_store_name || issue.issue_to_detail || issue.issue_to || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{issue.items?.length || 0} items</Badge>
                        </TableCell>
                        <TableCell>{issue.issued_by_name || '-'}</TableCell>
                        <TableCell>{getStatusBadge(issue.acknowledgment_status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewIssue(issue)}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenAcknowledge(issue)}
                              title="Acknowledge"
                              className="text-green-600"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDispute(issue)}
                              title="Dispute"
                              className="text-destructive"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Disputed Tab (Super Admin Only) */}
        {isSuperAdmin && (
          <TabsContent value="disputed">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Disputed Stock Issues</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredDisputed.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No disputed stock issues</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Issue #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>From Store</TableHead>
                        <TableHead>Disputed By</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDisputed.map((issue) => (
                        <TableRow key={issue.id}>
                          <TableCell className="font-mono font-medium">{issue.transaction_number}</TableCell>
                          <TableCell>{formatDate(issue.disputed_at)}</TableCell>
                          <TableCell>{issue.from_store_name || '-'}</TableCell>
                          <TableCell>{issue.disputed_by_name || '-'}</TableCell>
                          <TableCell className="max-w-xs truncate">{issue.dispute_reason}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewIssue(issue)}
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleOpenResolve(issue)}
                              >
                                Resolve
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* View Issue Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stock Issue Details - {selectedIssue?.transaction_number}</DialogTitle>
          </DialogHeader>

          {selectedIssue && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <span className="text-muted-foreground text-sm">Date</span>
                  <p className="font-medium">{formatDate(selectedIssue.date)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">From Store</span>
                  <p className="font-medium">{selectedIssue.from_store_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">To</span>
                  <p className="font-medium">{selectedIssue.to_store_name || selectedIssue.issue_to_detail || selectedIssue.issue_to || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Issued By</span>
                  <p className="font-medium">{selectedIssue.issued_by_name}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <span className="text-muted-foreground text-sm">Status</span>
                  <div className="mt-1">{getStatusBadge(selectedIssue.acknowledgment_status)}</div>
                </div>
                {selectedIssue.purpose && (
                  <div>
                    <span className="text-muted-foreground text-sm">Purpose</span>
                    <p className="font-medium">{selectedIssue.purpose}</p>
                  </div>
                )}
                {selectedIssue.received_by && (
                  <div>
                    <span className="text-muted-foreground text-sm">Received By</span>
                    <p className="font-medium">{selectedIssue.received_by}</p>
                  </div>
                )}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedIssue.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.item_name || item.item_id}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell>{item.unit_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {selectedIssue.notes && (
                <div className="p-3 bg-muted rounded-lg">
                  <span className="text-muted-foreground text-sm">Notes</span>
                  <p>{selectedIssue.notes}</p>
                </div>
              )}

              {selectedIssue.dispute_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-red-600 text-sm font-medium">Dispute Reason</span>
                  <p className="text-red-700">{selectedIssue.dispute_reason}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acknowledge Dialog - Item-wise */}
      <Dialog open={acknowledgeDialogOpen} onOpenChange={setAcknowledgeDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Acknowledge Stock Receipt
            </DialogTitle>
            <DialogDescription>
              Review and acknowledge each item individually. You can accept or reject items separately.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-lg flex justify-between items-center">
              <div>
                <p className="text-sm font-medium">{selectedIssue?.transaction_number}</p>
                <p className="text-sm text-muted-foreground">From: {selectedIssue?.from_store_name}</p>
              </div>
              <Badge variant="secondary">{selectedIssue?.items?.length || 0} items</Badge>
            </div>
            
            {/* Item-wise acknowledgment table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Issued Qty</TableHead>
                    <TableHead className="text-right">Received Qty</TableHead>
                    <TableHead className="text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedIssue?.items?.map((item, index) => {
                    const itemAck = itemAcknowledgments[item.item_id] || { action: 'accept', received_quantity: item.quantity };
                    return (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.item_name || item.item_id}</TableCell>
                        <TableCell className="text-right">{item.quantity} {item.unit_name}</TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min="0"
                            max={item.quantity}
                            step="0.01"
                            value={itemAck.received_quantity}
                            onChange={(e) => handleItemAckChange(item.item_id, 'received_quantity', parseFloat(e.target.value) || 0)}
                            className="w-20 text-right"
                            disabled={itemAck.action === 'reject'}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-center gap-2">
                            <Button
                              variant={itemAck.action === 'accept' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleItemAckChange(item.item_id, 'action', 'accept')}
                              className={itemAck.action === 'accept' ? 'bg-green-600 hover:bg-green-700' : ''}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Accept
                            </Button>
                            <Button
                              variant={itemAck.action === 'reject' ? 'destructive' : 'outline'}
                              size="sm"
                              onClick={() => handleItemAckChange(item.item_id, 'action', 'reject')}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={acknowledgeNotes}
                onChange={(e) => setAcknowledgeNotes(e.target.value)}
                placeholder="Any comments about the received stock..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAcknowledgeDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAcknowledgeAll} 
              disabled={processing}
              variant="secondary"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Accept All
            </Button>
            <Button onClick={handleAcknowledgeItemWise} disabled={processing} className="bg-green-600 hover:bg-green-700">
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Submit Acknowledgment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Dialog */}
      <Dialog open={disputeDialogOpen} onOpenChange={setDisputeDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Dispute Stock Issue
            </DialogTitle>
            <DialogDescription>
              Report a discrepancy with stock issue {selectedIssue?.transaction_number}. Admin will review and resolve.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for Dispute *</Label>
              <Textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                placeholder="Describe the issue (e.g., quantity mismatch, damaged items, wrong items received)..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDispute} disabled={processing} variant="destructive">
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Submit Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Dispute Dialog (Super Admin) */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Resolve Dispute - {selectedIssue?.transaction_number}</DialogTitle>
            <DialogDescription>
              Review the dispute and decide on the resolution action.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedIssue?.dispute_reason && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-red-600 text-sm font-medium">Dispute Reason:</span>
                <p className="text-red-700">{selectedIssue.dispute_reason}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Resolution Action</Label>
              <Select value={resolveAction} onValueChange={setResolveAction}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="accept">Accept as-is (Override dispute)</SelectItem>
                  <SelectItem value="reject">Reject & Reverse Stock</SelectItem>
                  <SelectItem value="adjust">Adjust Quantities</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Resolution Details *</Label>
              <Textarea
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                placeholder="Explain the resolution decision..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleResolve} disabled={processing}>
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Resolve Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockAcknowledgmentPage;
